<template>
  <div class="au-editor">
    <audit-container class="editor-audit-container">
      <template v-slot:topBar>
        <steps-controller
          :disabled="disabledSteps"
          :use-custom-next="useCustomNextEvent"
          @custom-next="handleCustomNext"
        ></steps-controller>
      </template>
      <template v-slot:main>
        <design-editor v-show="currentStep === STEP_NAME.EDITOR"></design-editor>
        <vdp-group v-show="currentStep === STEP_NAME.VDP_TABLE"></vdp-group>
        <approval-review
          v-if="currentStep === STEP_NAME.APPROVAL"
          @change-checkout-checkbox="handleCheckoutCheckboxChange"
          @checkout-finish="handleCheckoutFinish"
        ></approval-review>
      </template>
    </audit-container>
    <vdp-validation-modal>
      <template v-slot:vdp_guide>
        <slot name="vdp_guide"></slot>
      </template>
    </vdp-validation-modal>
    <guide-modal></guide-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, type Ref, onUnmounted, onMounted, provide, watch } from 'vue';

import emitter from '../core/emitter';
import pinia from '../core/pinia';

import { useConfigStore, useEditorStore, useLineItemStore, useStepsStore, useVdpTableStore } from '../stores';

import AuditContainer from '../components/ui/AuditContainer.vue';
import DesignEditor from '../components/editor/DesignEditor.vue';
import ApprovalReview from '../components/approval-review/ApprovalReview.vue';
import StepsController from '../components/ui/StepsController.vue';
import VdpGroup from '../components/vdp-table/VdpGroup.vue';
import VdpValidationModal from '../components/modals/VdpValidationModal.vue';
import GuideModal from '../components/modals/GuideModal.vue';

import { E_EDITOR_VIEW_EVENT } from '../types/components-events.enums';
import { E_EDITOR_CUSTOM_EVENT } from '../types/custom-events.enums';
import { E_STEP_NAME } from '../types/components.enums';
import type { ILineItem } from '../types/line-item.types';
import type { TCheckboxGroupSelectedItems } from '../types/checkbox-group.types';
import type { IProductItems } from '../types/design-editor';

import EditorIframeApiService from '../services/editor-iframe-api.service';

import DesignEditorLoader from '../utils/design-editor-loader';
import loadDatasets from '../utils/load-datasets';
import { InnerVdpDataFormatter } from '../utils/inner-vdp-data-formatter';
import { InnerDatasetFormatter } from '../utils/inner-dataset-data-formatter';


export default defineComponent({
  name: 'AuEditor',
  components: {
    'design-editor': DesignEditor,
    'audit-container': AuditContainer,
    'steps-controller': StepsController,
    'approval-review': ApprovalReview,
    'vdp-group': VdpGroup,
    'vdp-validation-modal': VdpValidationModal,
    'guide-modal': GuideModal,
  },
  emits: {
    [E_EDITOR_VIEW_EVENT.CHANGE_CHECKOUT_CHECKBOX]: (checkboxGroupSelectedItems: TCheckboxGroupSelectedItems) => checkboxGroupSelectedItems,
    [E_EDITOR_VIEW_EVENT.CHECKOUT_FINISH]: (lineItem: ILineItem | null) => lineItem,
    [E_EDITOR_VIEW_EVENT.DUPLICATE]: (lineItem: ILineItem | null) => lineItem,
    [E_EDITOR_VIEW_EVENT.CUSTOM_NEXT]: (lineItem: ILineItem | null) => lineItem,
  },
  setup(_, { emit, expose }) {
    const STEP_NAME = E_STEP_NAME;
    
    const configStore = useConfigStore(pinia);
    const stepsStore = useStepsStore(pinia);
    const lineItemStore = useLineItemStore(pinia);
    const vdpTableStore = useVdpTableStore(pinia);
    const editorStore = useEditorStore(pinia);
    
    const disabledSteps: Ref<boolean> = ref(true);
    const iframeApiLoadStatus: Ref<'not' | 'pending' | 'loaded'> = ref('not');
    const scriptElement: Ref<HTMLScriptElement | null> = ref(null);
    const currentSearch: Ref<string, string> = ref(window.location.search);

    const isRestore = computed(() => {
      const searchParams = new URLSearchParams(currentSearch.value);
      return !!searchParams.get('key') && !!searchParams.get('stateId');
    });
    const useCustomNextEvent = computed(() => configStore.getUseCustomNextEvent);
    const lineItemUpdateData = computed(() => configStore.getInitDataForLineItem);
    const currentStep = computed(() => (stepsStore.getCurrentStep) ? stepsStore.getCurrentStep.toLowerCase() : null);
    const designEditorUrl = computed(() => configStore.getEditorUrl);
    const iframeApiUrl = computed(() => {
      if (!!designEditorUrl.value) {
        return `${designEditorUrl.value}/Resources/Generated/IframeApi.js`;
      } else {
        return null;
      }
    });

    provide('iframeApiLoadStatus', iframeApiLoadStatus);

    emitter.on(E_EDITOR_CUSTOM_EVENT.EDITOR_LOAD, () => {
      disabledSteps.value = false;
    });

    const editorIframeApiService = new EditorIframeApiService();
    const designEditorLoader = new DesignEditorLoader();

    // watch(iframeApiUrl, async (url) => {
    //   await loadIframeApiIfNotLoaded(url);
    // }, { immediate: true, deep: true });

    watch(isRestore, (value) => {
      editorStore.setRestoreState(value);
    }, { immediate: true });

    function handleCheckoutCheckboxChange(checkboxGroupSelectedItems: TCheckboxGroupSelectedItems) {
      emit(E_EDITOR_VIEW_EVENT.CHANGE_CHECKOUT_CHECKBOX, checkboxGroupSelectedItems);
      return checkboxGroupSelectedItems;
    }

    function handleCheckoutFinish(lineItem: ILineItem | null) {
      emit(E_EDITOR_VIEW_EVENT.CHECKOUT_FINISH, lineItem);
      return lineItem;
    }

    function handleDuplicate(lineItem: ILineItem | null) {
      emit(E_EDITOR_VIEW_EVENT.DUPLICATE, lineItem);
      return lineItem;
    }

    function handleCustomNext(lineItem: ILineItem | null) {
      emit(E_EDITOR_VIEW_EVENT.CUSTOM_NEXT, lineItem);
      return lineItem;
    }

    function iframeApiLoaded() {
      iframeApiLoadStatus.value = 'loaded';
    }

    function iframeApiPending() {
      iframeApiLoadStatus.value = 'pending';
    }

    async function loadIframeApiIfNotLoaded(iframeApiUrlString: string | null) {
      const isNotLoad = iframeApiLoadStatus.value === 'not' || iframeApiLoadStatus.value === 'pending';
      if (iframeApiUrlString && isNotLoad) {
        iframeApiPending();
        await configStore.setupGalleryRootFolder();
        await configStore.setupCustomButtonsFromSurfaceData();
        try {
          const designAtomsDataset = await getVdpData();
          if (designAtomsDataset) {
            const innerDataset = new InnerDatasetFormatter();
            innerDataset.fromDesignAtomsDataset(designAtomsDataset);
            const preparedDataset = InnerVdpDataFormatter({
              vdpDataWithSurface: innerDataset.datasets,
              useOriginalValue: true,
            });
            loadDatasets(preparedDataset.datasets, preparedDataset.surfacesData);
          }
        } catch(error) {
          console.error(error);
        }
        if (EditorIframeApiService.status.value === 'finally') {
          iframeApiLoaded();
        }
        if (EditorIframeApiService.status.value === null) {
          await editorIframeApiService.load(designEditorUrl.value, iframeApiUrlString, iframeApiLoaded, scriptElement);
        }
        setTimeout(() => {
          designEditorLoader.setup();
          designEditorLoader.addTemplate();
        }, 1000);
      }
    }

    async function saveDesign(): Promise<string> {
      emitter.emit(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN);
      return new Promise((res, rej) => {
        try {
          emitter.on(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS, (stateId: any) => {
            res(stateId as string);
            emitter.off(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS);
            return stateId as string;
          });
        } catch (error) {
          rej(error);
        }
      });
    }

    async function getProductItems(): Promise<IProductItems> {
      emitter.emit(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS);
      return new Promise((res, rej) => {
        try {
          emitter.on(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS_SUCCESS, (productItems): IProductItems => {
            res(productItems);
            emitter.off(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS_SUCCESS);
            return productItems;
          });
        } catch (error) {
          rej(error);
        }
      });
    }

    async function getVdpData() {
      return await vdpTableStore.fetchVdpData();
    }

    onMounted(async () => {
      await loadIframeApiIfNotLoaded(iframeApiUrl.value);
      lineItemStore.updateLineItem(lineItemUpdateData.value);
      if (isRestore.value) {
        const designAtomsDataset = await getVdpData();
        if (designAtomsDataset) {
          const innerDataset = new InnerDatasetFormatter();
          innerDataset.fromDesignAtomsDataset(designAtomsDataset);
          const preparedDataset = InnerVdpDataFormatter({
            vdpDataWithSurface: innerDataset.datasets,
            useOriginalValue: true,
          });
          loadDatasets(preparedDataset.datasets, preparedDataset.surfacesData);
        }
      }
    });

    onUnmounted(() => {
      if (scriptElement.value) {
        scriptElement.value.remove();
      }
    });

    expose({
      getProductItems,
      saveDesign,
      loadDatasets,
      InnerVdpDataFormatter,
    });

    return {
      STEP_NAME,
      handleCheckoutCheckboxChange,
      handleCheckoutFinish,
      handleDuplicate,
      handleCustomNext,
      saveDesign,
      currentStep,
      disabledSteps,
      useCustomNextEvent,
    };
  },
});
</script>

<style lang="scss">
.editor-frame-container.masked-element {
  background-color: #ffffff;
}

div:has(>.loader-wraper) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.loader-wraper {
  border: 1px solid;
  border-color: $brand-color;
  border-radius: 50%;
  padding: 10px;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 50px;

  svg {
    color: $brand-color!important;
  }

  .loader-border {
    border-color: $brand-color-50!important;
  }
}

.mask.warning .loader-wraper {
  border-color: $brand-color;
}
</style>